import { AlertCircle, ArrowRightCircle, CheckCircle2, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CustomFieldsService from '../../services/customFields/customFieldsService';
import type { CustomFieldKind, CustomFieldPreviewResponse } from './types';

const KIND_LABEL: Record<CustomFieldKind, string> = {
  string: 'String',
  number: 'Number',
  option: 'Option',
  user: 'User',
  array: 'Array',
  cascading: 'Cascading',
};

export interface LivePreviewProps {
  /** Current Jira field id selected in the form. `null` clears the preview. */
  jiraFieldId: string | null;
  /** Currently selected kind in the form (so we can show "Switch" only when it differs). */
  selectedKind: CustomFieldKind;
  /** Called with the recommended kind so the form can pre-fill / switch. */
  onRecommendKind: (kind: CustomFieldKind) => void;
  /**
   * Called whenever the preview is actively loading. Lets the parent disable
   * the Register button to prevent the user from registering with the wrong
   * kind before the recommendation lands.
   */
  onLoadingChange?: (loading: boolean) => void;
  /** Debounce window before kicking off the network request (ms). */
  debounceMs?: number;
}

/**
 * Live Preview block for the "Add custom field" form.
 *
 * Workflow:
 *   1. User picks a Jira field → 500 ms debounce starts.
 *   2. Component fires `GET /api/custom-fields/preview?jiraFieldId=...`,
 *      shows a skeleton, and tells the parent the form is loading.
 *   3. Server returns up to 3 sample values + a recommended kind.
 *   4. Component renders the samples and, if the recommendation differs from
 *      the user's current selection, offers a one-click "Switch" button.
 *
 * Defensive guards (per design):
 *   - Debounce so dropdown hovering doesn't hammer Jira.
 *   - AbortController so an in-flight request is dropped when the user picks a
 *     different field mid-fetch.
 *   - Server-side fallback returns an empty samples list with a helpful hint
 *     instead of throwing on Jira errors — render the hint, never the error.
 */
export const LivePreview: React.FC<LivePreviewProps> = ({
  jiraFieldId,
  selectedKind,
  onRecommendKind,
  onLoadingChange,
  debounceMs = 500,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CustomFieldPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<number | null>(null);
  // Keep a ref to onLoadingChange so the debounce effect doesn't restart when
  // the parent passes a new function on every render.
  const onLoadingRef = useRef(onLoadingChange);
  onLoadingRef.current = onLoadingChange;
  const onRecommendRef = useRef(onRecommendKind);
  onRecommendRef.current = onRecommendKind;

  const setBusy = useCallback((isBusy: boolean) => {
    setLoading(isBusy);
    if (onLoadingRef.current) onLoadingRef.current(isBusy);
  }, []);

  useEffect(() => {
    // Field cleared → drop any in-flight request and reset.
    if (!jiraFieldId) {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
      timerRef.current = null;
      abortRef.current = null;
      setData(null);
      setError(null);
      setBusy(false);
      return;
    }

    setBusy(true);
    setError(null);

    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      // Cancel any previous request — the user just changed selection.
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      CustomFieldsService.getFieldPreview(jiraFieldId, ac.signal)
        .then((payload) => {
          if (ac.signal.aborted) return;
          setData(payload);
          setError(null);
          setBusy(false);
          // Auto pre-fill kind on the parent so the dropdown shows a sensible
          // default before the user even reads the preview.
          if (payload.recommendedKind) {
            onRecommendRef.current(payload.recommendedKind);
          }
        })
        .catch((e: unknown) => {
          if ((e as { name?: string })?.name === 'AbortError') return;
          setError(e instanceof Error ? e.message : 'Preview failed');
          setData(null);
          setBusy(false);
        });
    }, debounceMs);

    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
    // setBusy is stable (memoised); we deliberately exclude onRecommendKind/onLoadingChange
    // from the deps because they're consumed via refs above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jiraFieldId, debounceMs]);

  if (!jiraFieldId) return null;

  const showRecommendBanner = data && data.recommendedKind && data.recommendedKind !== selectedKind;

  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        borderRadius: 8,
        border: '1px solid var(--color-border, #444)',
        background: 'var(--color-bg, #121212)',
        fontSize: 13,
      }}
      aria-live="polite"
      data-testid="live-preview"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Live preview
        </strong>
        {loading ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
            <Loader2 className="animate-spin" size={12} />
            Reading 3 sample values from Jira…
          </span>
        ) : data ? (
          <span style={{ fontSize: 11, color: 'var(--color-text-secondary, #888)' }}>
            {data.samples.length} sample task{data.samples.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>

      {loading ? (
        <div data-testid="live-preview-skeleton">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                height: 18,
                marginBottom: 6,
                borderRadius: 4,
                background:
                  'linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.10), rgba(255,255,255,0.05))',
              }}
            />
          ))}
        </div>
      ) : error ? (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--color-text-secondary, #aaa)',
          }}
        >
          <AlertCircle size={14} />
          {error}
        </div>
      ) : data ? (
        <>
          {data.samples.length > 0 ? (
            <div
              style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 4, columnGap: 12 }}
            >
              {data.samples.map((s) => (
                <React.Fragment key={s.issueKey}>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono, monospace)',
                      fontSize: 12,
                      color: 'var(--color-text-secondary, #888)',
                    }}
                  >
                    {s.issueKey}
                  </span>
                  <span style={{ fontSize: 13 }}>
                    {s.display && s.display.trim() !== '' ? (
                      s.display
                    ) : (
                      <span
                        style={{ color: 'var(--color-text-secondary, #888)', fontStyle: 'italic' }}
                      >
                        (empty)
                      </span>
                    )}
                  </span>
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: 'var(--color-text-secondary, #aaa)',
              }}
            >
              <AlertCircle size={14} />
              {data.hint || 'No sample data — pick the kind manually.'}
            </div>
          )}

          {showRecommendBanner ? (
            <div
              style={{
                marginTop: 10,
                padding: '8px 10px',
                borderRadius: 6,
                background: 'rgba(245, 158, 11, 0.12)',
                border: '1px solid rgba(245, 158, 11, 0.4)',
                color: '#f59e0b',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
              }}
              role="status"
            >
              <AlertCircle size={14} />
              <span style={{ flex: 1 }}>
                {data.hint
                  ? `${data.hint} Recommended kind: ${KIND_LABEL[data.recommendedKind]}.`
                  : `Recommended kind: ${KIND_LABEL[data.recommendedKind]}.`}
              </span>
              <button
                type="button"
                onClick={() => onRecommendKind(data.recommendedKind)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  borderRadius: 4,
                  border: '1px solid #f59e0b',
                  background: 'transparent',
                  color: '#f59e0b',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                <ArrowRightCircle size={12} />
                Switch to {KIND_LABEL[data.recommendedKind]}
              </button>
            </div>
          ) : data.samples.length > 0 ? (
            <div
              style={{
                marginTop: 10,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: '#22c55e',
              }}
            >
              <CheckCircle2 size={14} />
              Looks good — {KIND_LABEL[selectedKind]} matches the data.
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
